// components/Billing/ExtrasTab.jsx
import React, { useEffect, useState } from "react";
import { Box, Stack, TextField, Button, MenuItem } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTheme } from "@mui/material/styles";
import moment from "moment";

import {
  listAllExtraInvoices,
  resetAllExtraInvoices,
} from "../../../store/actions";
import { tokens } from "../../../theme";
import { PaginationFooter, tableStyle } from "../../../components";
import AddExtraBillModal from "./AddExtraBillModal";

// Helper function for formatting Unix timestamps
const formatUnixDate = (timestamp) => {
  if (!timestamp) return "N/A";
  return moment.unix(timestamp).format("DD MMM YYYY");
};

// Helper function for unique activity names
const getUniqueActivityNames = (extras) => {
  if (!extras || extras.length === 0) return "-";
  const baseItems = extras.filter((item) => !item.particulars.includes("GST"));
  const activityNames = baseItems.map(
    (item) => item.particulars.split(" (")[0]
  );
  const uniqueNames = [...new Set(activityNames)];
  return uniqueNames.join(", ");
};

// Columns for the DataGrid
const columns = [
  {
    field: "inv_date",
    headerName: "Invoice Date",
    flex: 1,
    valueGetter: (params) => formatUnixDate(params.value),
  },
  { field: "inv_no", headerName: "Invoice No.", flex: 1.2 },
  { field: "booking_id", headerName: "Booking ID", flex: 1.2 },
  {
    field: "activity_names",
    headerName: "Activities",
    flex: 1,
    valueGetter: (params) => getUniqueActivityNames(params.row.extras),
  },
  {
    field: "customer_name",
    headerName: "Customer Name",
    flex: 1,
  },
  {
    field: "amount",
    headerName: "Amount",
    flex: 0.75,
    type: "number",
    valueGetter: (params) => params.row.total || 0,
  },
  {
    field: "pmt_status",
    headerName: "Payment Status",
    flex: 0.8,
    valueGetter: (params) => params.value?.toUpperCase() || "N/A",
  },
];

const ExtrasTab = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // 🔹 Filter states
  const [bookingId, setBookingId] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [pmt_status, setPmt_status] = useState("");

  // 🔹 Redux state
  const { extraInvoicesList, pageNumber, pageSize, count, isLoading } =
    useSelector(({ loading, admin }) => {
      const extraInvoice = admin?.extraInvoice || {};
      const {
        list = [],
        pageNumber = 1,
        pageSize = 20,
        totalCount = 0,
      } = extraInvoice;

      return {
        isLoading: loading,
        extraInvoicesList: list,
        pageNumber,
        pageSize,
        count: totalCount,
      };
    });

  // 🔹 Initial fetch
  useEffect(() => {
    fetchMoreData({ pageNumber: 1 });
  }, [dispatch]);

  // 🔹 Fetch data with filters
  const fetchMoreData = (page) => {
    const filters = {
      page_number: page?.pageNumber || 1,
      page_size: page?.pageSize || pageSize,
      booking_id: bookingId || null,
      customer_name: customerName || null,
      pmt_status: pmt_status || null,
    };

    // Add date range filters if both are provided
    if (startDate && endDate) {
      filters.start_date = moment(startDate).startOf("day").unix();
      filters.end_date = moment(endDate).endOf("day").unix();
    } else if (startDate && !endDate) {
      filters.start_date = moment(startDate).startOf("day").unix();
    } else if (!startDate && endDate) {
      filters.end_date = moment(endDate).endOf("day").unix();
    }

    // Remove empty/null keys
    Object.keys(filters).forEach(
      (key) =>
        (filters[key] === null || filters[key] === "") && delete filters[key]
    );

    dispatch(listAllExtraInvoices(filters));
  };

  // 🔹 Search button
  const handleFilter = () => {
    dispatch(resetAllExtraInvoices());
    fetchMoreData({ pageNumber: 1 });
  };

  // 🔹 Reset filters
  const handleReset = () => {
    setBookingId("");
    setStartDate(null);
    setEndDate(null);
    setCustomerName("");
    setPmt_status("");
    dispatch(resetAllExtraInvoices());
    fetchMoreData({ pageNumber: 1 });
  };

  // 🔹 View individual invoice
  const handleViewExtra = (invoice) => {
    navigate(`/billing/extras/${invoice._id}`);
  };

  // 🔹 Pagination footer
  const CustomFooter = () => (
    <PaginationFooter
      count={count}
      pageSize={pageSize}
      pageNumber={pageNumber}
      fetchMoreData={(page) => fetchMoreData(page)}
    />
  );

  return (
    <Box m="20px">
      {/* 🔹 Filter bar */}
      <Stack
        direction="row"
        spacing={1}
        my={2}
        alignItems="center"
        flexWrap="wrap"
      >
        <TextField
          label="Booking ID"
          size="small"
          value={bookingId}
          onChange={(e) => setBookingId(e.target.value)}
        />

        <TextField
          label="From Date"
          type="date"
          size="small"
          value={startDate || ""}
          onChange={(e) => setStartDate(e.target.value || null)}
          InputLabelProps={{ shrink: true }}
          sx={{
            '& input[type="date"]::-webkit-calendar-picker-indicator': {
              filter:
                theme.palette.mode === "dark" ? "invert(1)" : "invert(0)",
            },
          }}
        />

        <TextField
          label="To Date"
          type="date"
          size="small"
          value={endDate || ""}
          onChange={(e) => setEndDate(e.target.value || null)}
          InputLabelProps={{ shrink: true }}
          sx={{
            '& input[type="date"]::-webkit-calendar-picker-indicator': {
              filter:
                theme.palette.mode === "dark" ? "invert(1)" : "invert(0)",
            },
          }}
        />

        <TextField
          label="Customer Name"
          size="small"
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
        />

        <TextField
          select
          label="Payment Status"
          size="small"
          value={pmt_status}
          onChange={(e) => setPmt_status(e.target.value)}
        >
          <MenuItem value="">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span>All Status</span>
            </Box>
          </MenuItem>
          <MenuItem value="paid">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span>Paid</span>
            </Box>
          </MenuItem>
          <MenuItem value="unpaid">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span>Unpaid</span>
            </Box>
          </MenuItem>
        </TextField>

        <Button variant="contained" onClick={handleFilter}>
          Search
        </Button>
        <Button variant="outlined" onClick={handleReset} size="small">
          Reset
        </Button>

        {/* Push Add Extra Bill button to right */}
        <Box sx={{ flexGrow: 1 }} />
        <AddExtraBillModal />
      </Stack>

      {/* 🔹 Data Table */}
      <Box
        sx={{
          height: "68vh",
          width: "100%",
          ...tableStyle(colors),
        }}
      >
        <DataGrid
          loading={isLoading}
          rows={extraInvoicesList || []}
          columns={columns}
          components={{ Footer: CustomFooter }}
          getRowId={(row) => row._id}
          onRowClick={(params) => handleViewExtra(params.row)}
          sx={{
            "& .MuiDataGrid-row:hover": { cursor: "pointer" },
          }}
        />
      </Box>
    </Box>
  );
};

export default ExtrasTab;
